// PopUpNotificacionWeb.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./PopUpNotificacionWeb.css";

const API_URL = process.env.REACT_APP_API_POPUP_ACTUAL;

const getTipo = (w) => {
    if (w >= 992) return "PC";
    if (w >= 768) return "TABLET";
    return "CEL";
};

const hoyLocal = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD

export default function PopUpNotificacionWeb() {
    const [visible, setVisible] = useState(false);
    const [img, setImg] = useState(null);

    // lista completa del back (TODAS las del día)
    const [listaAll, setListaAll] = useState([]);

    // lista del tipo actual (PC/TABLET/CEL) sin refetch
    const [lista, setLista] = useState([]);

    const [loadingImg, setLoadingImg] = useState(false);
    const [tipo, setTipo] = useState(() => getTipo(window.innerWidth));

    const hoy = useMemo(() => hoyLocal(), []);

    // ✅ guardo el popup actual (no depende de lista/index que cambia con resize)
    const currentRef = useRef(null); // { id, idx, tipo }

    // ✅ clave por día + id (no por tipo)
    const storageKey = (id) => `popupWebMostrado_${hoy}_${id}`;

    const buscarSiguiente = (arr, startIndex) => {
        for (let i = startIndex; i < arr.length; i++) {
            const item = arr[i];
            if (!item?.id) continue;
            if (localStorage.getItem(storageKey(item.id))) continue; // ya visto hoy
            return i;
        }
        return -1;
    };

    const mostrarEn = (arr, idx) => {
        const item = arr[idx];
        if (!item) return;

        setLoadingImg(true);

        const pre = new window.Image();
        pre.src = item.imagenBase64;

        pre.onload = () => {
            currentRef.current = { id: item.id, idx, tipo: (item.tipo || "").toUpperCase() };
            setImg(item.imagenBase64);
            setVisible(true);
            setLoadingImg(false);
        };

        pre.onerror = () => {
            setLoadingImg(false);
            const next = buscarSiguiente(arr, idx + 1);
            if (next !== -1) mostrarEn(arr, next);
            else {
                setVisible(false);
                setImg(null);
                currentRef.current = null;
            }
        };
    };

    const cerrar = () => {
        const current = currentRef.current;

        // ✅ marco como visto el que realmente estaba viendo (aunque cambie la resolución)
        if (current?.id) localStorage.setItem(storageKey(current.id), "1");

        setVisible(false);
        setImg(null);

        // ✅ si cambió el tipo mientras estaba abierto, arrancamos desde 0 en la lista actual
        const startFrom =
            current && current.tipo === tipo ? current.idx + 1 : 0;

        const next = buscarSiguiente(lista, startFrom);
        if (next !== -1) {
            setTimeout(() => mostrarEn(lista, next), 120);
        } else {
            currentRef.current = null;
        }
    };

    // bloquear scroll cuando el modal está visible
    useEffect(() => {
        if (!visible) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [visible]);

    // ✅ 1 sola consulta al back (trae TODO lo activo hoy)
    useEffect(() => {
        const controller = new AbortController();

        const cargar = async () => {
            try {
                const res = await fetch(API_URL, { signal: controller.signal });
                if (!res.ok) return;

                const data = await res.json(); // array
                if (!Array.isArray(data) || data.length === 0) return;

                // seguridad por fechas (si el back ya filtra, igual ok)
                const hoyDate = new Date(hoy);
                const activos = data.filter((p) => {
                    const inicio = new Date(p.fechaInicio);
                    const fin = new Date(p.fechaFin);
                    return hoyDate >= inicio && hoyDate <= fin;
                });

                if (activos.length === 0) return;

                setListaAll(activos);
            } catch (e) {
                if (e.name !== "AbortError") console.error("Popup web error:", e);
            }
        };

        cargar();
        return () => controller.abort();
    }, [hoy]);

    // ✅ resize: cambia tipo SIN refetch
    useEffect(() => {
        const onResize = () => setTipo(getTipo(window.innerWidth));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // ✅ cuando cambia tipo o llega listaAll: recalculo lista del tipo
    // ⚠️ PERO: si ya hay un popup visible, NO lo piso.
    useEffect(() => {
        if (!listaAll.length) return;

        const filtrados = listaAll.filter(
            (p) => (p.tipo || "").toUpperCase() === tipo
        );

        setLista(filtrados);

        // si no hay nada para este tipo, no hago nada
        if (filtrados.length === 0) return;

        // ✅ si ya está visible, no reinicio (solo cambio para el siguiente cierre)
        if (visible) return;

        const first = buscarSiguiente(filtrados, 0);
        if (first === -1) return;

        mostrarEn(filtrados, first);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipo, listaAll]);

    if (loadingImg) return null;
    if (!visible || !img) return null;

    return (
        <div className="popup-overlay" onClick={cerrar}>
            <div className="popup-card" onClick={(e) => e.stopPropagation()}>
                <button
                    className="popup-close"
                    onClick={(e) => {
                        e.stopPropagation();
                        cerrar();
                    }}
                    aria-label="Cerrar"
                >
                    ✕
                </button>

                <div className="popup-media">
                    <img src={img} alt="Notificación" className="popup-img" />
                </div>

                <button
                    className="popup-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        cerrar();
                    }}
                >
                    Cerrar
                </button>
            </div>
        </div>
    );
}
